import React from "react";

import { Box, ButtonBase, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ClosedCaptionIcon from "@mui/icons-material/ClosedCaption";
import TitleIcon from "@mui/icons-material/Title";
import GifBoxIcon from "@mui/icons-material/GifBox";
import { useNavigate } from "react-router";

export type EditorTool = "Captions" | "Text" | "Elements";

const TOOLS: { id: EditorTool; label: string; icon: React.ReactNode }[] = [
  { id: "Captions", label: "Captions", icon: <ClosedCaptionIcon /> },
  { id: "Text", label: "Text", icon: <TitleIcon /> },
  { id: "Elements", label: "Stickers", icon: <GifBoxIcon /> },
];

interface ToolRailProps {
  selected: EditorTool;
  onSelect: (tool: EditorTool) => void;
}

export default function ToolRail({ selected, onSelect }: ToolRailProps) {
  const navigate = useNavigate();
  return (
    <Box
      component="nav"
      sx={{
        width: 76,
        flexShrink: 0,
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        alignItems: "center",
        gap: 0.5,
        py: 1.5,
        borderRight: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      <ButtonBase
        onClick={() => navigate("/episodes")}
        sx={{ mb: 2, borderRadius: 2, p: 0.5 }}
        aria-label="Go to episodes"
      >
        <img
          src="/logo.png"
          alt="Podcast Assistant"
          style={{ width: 32, height: 32, objectFit: "contain" }}
        />
      </ButtonBase>
      {TOOLS.map((tool) => {
        const isSelected = tool.id === selected;
        return (
          <ButtonBase
            key={tool.id}
            onClick={() => onSelect(tool.id)}
            aria-pressed={isSelected}
            sx={{
              width: 62,
              py: 1,
              borderRadius: 2,
              display: "flex",
              flexDirection: "column",
              gap: 0.25,
              color: isSelected ? "primary.main" : "text.secondary",
              bgcolor: (theme) =>
                isSelected
                  ? alpha(theme.palette.primary.main, 0.12)
                  : "transparent",
              transition: "background-color 120ms, color 120ms",
              "&:hover": {
                bgcolor: (theme) =>
                  isSelected
                    ? alpha(theme.palette.primary.main, 0.16)
                    : theme.palette.action.hover,
                color: isSelected ? "primary.main" : "text.primary",
              },
            }}
          >
            {tool.icon}
            <Typography
              variant="caption"
              sx={{ fontSize: 11, fontWeight: isSelected ? 700 : 500 }}
            >
              {tool.label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
}
